import React, { useEffect, useRef, useState } from 'react';
import { makeApiRequests } from '../../helpers/api';
import './service-work-orders.css';

const base = '/service-work-orders';
const emptyRow = () => ({ Description: '', Location: '', Part: '', 'Repair Method': '', Cause: '', Hours: '' });
const showLocation = value => Array.isArray(value) ? value.join(', ') : value || '';
const storeName = value => value && typeof value === 'object' ? value.location || 'Assigned location' : 'Assigned location';
const rowFields = ['Description', 'Location', 'Part', 'Repair Method', 'Cause', 'Hours'];

export default function ServiceWorkOrders() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [more, setMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rows, setRows] = useState([emptyRow()]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const searchNumber = useRef(0);
  const loadNumber = useRef(0);
  const saveLock = useRef(false);

  const loadSearch = async text => {
    const sequence = ++searchNumber.current;
    setSearching(true);
    const result = await makeApiRequests({ endpoint: base + '?q=' + encodeURIComponent(text), method: 'GET' });
    if (sequence !== searchNumber.current) return;
    setSearching(false);
    if (result.error) { setResults([]); setError(result.error); return; }
    setResults(result.response.items || []);
    setMore(!!result.response.hasMore);
  };
  const applyRecord = record => {
    setSelected(record);
    setRows(record.serviceOrderReport && record.serviceOrderReport.length ? record.serviceOrderReport.map(row => ({ ...row })) : [emptyRow()]);
    setDirty(false);
  };
  const openRecord = async (id, discard = false) => {
    if (saveLock.current) return;
    if (dirty && !discard && !window.confirm('Discard your unsaved work-order changes?')) return;
    const sequence = ++loadNumber.current;
    setLoading(true); setError(''); setMessage('');
    const result = await makeApiRequests({ endpoint: base + '/' + id, method: 'GET' });
    if (sequence !== loadNumber.current) return;
    setLoading(false);
    if (result.error) { setError(result.error); return; }
    applyRecord(result.response);
  };
  useEffect(() => {
    loadSearch('');
    const id = new URLSearchParams(window.location.search).get('contractId');
    if (id && /^[a-f0-9]{24}$/i.test(id)) openRecord(id);
    return () => { searchNumber.current += 1; loadNumber.current += 1; };
    // Load once; searches and customer selection are explicit actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const guard = event => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);
  const changeRow = (index, key, value) => {
    setRows(previous => previous.map((row, i) => i === index ? { ...row, [key]: value } : row));
    setDirty(true); setMessage('');
  };
  const submit = async event => {
    event.preventDefault();
    if (!selected || loading || saveLock.current) return;
    if (rows.some(row => !String(row.Description || '').trim())) { setError('Enter a description for every item.'); return; }
    saveLock.current = true; setSaving(true); setError(''); setMessage('');
    try {
      const result = await makeApiRequests({ endpoint: base + '/' + selected._id, method: 'PUT', requestBody: { serviceOrderReport: rows, reportVersion: selected.reportVersion } });
      if (result.error) { setError(result.error); return; }
      if (!result.response || !result.response._id || !result.response.reportVersion) { setError('The server did not confirm the save. Reload the saved version to check it.'); return; }
      applyRecord(result.response);
      setResults(previous => previous.map(record => String(record._id) === String(result.response._id) ? result.response : record));
      setMessage('Work order saved.');
    } finally { saveLock.current = false; setSaving(false); }
  };
  const address = selected ? [selected.propertyAddress, selected.propertyCity, selected.propertyState, selected.zipCode].filter(Boolean).join(', ') : '';

  return <main className="mhc-work-orders">
    <div className="mwo-heading"><div><p className="mwo-kicker">MHC • CUSTOMER SERVICE</p><h1>Service work orders</h1><p>Create and update work orders for customers at your assigned locations.</p></div></div>
    {error && <div className="mwo-alert mwo-error" role="alert">{error}</div>}
    {message && <div className="mwo-alert mwo-success" role="status">{message}</div>}
    <div className="mwo-layout">
      <aside className="mwo-search mwo-card">
        <h2>Find a customer</h2>
        <form onSubmit={event => { event.preventDefault(); setError(''); loadSearch(query); }}>
          <label htmlFor="mwo-query">Customer name or property address</label>
          <div className="mwo-search-row"><input id="mwo-query" value={query} onChange={event => setQuery(event.target.value)} maxLength={120} /><button type="submit" disabled={searching}>Search</button></div>
        </form>
        {searching ? <p role="status">Loading customers…</p> : <>
          {results.length === 0 && <p>No matching customers in your assigned locations.</p>}
          <div className="mwo-results">{results.map(record => <button key={record._id} type="button" className={'mwo-customer ' + (selected && String(selected._id) === String(record._id) ? 'is-selected' : '')} disabled={saving || loading} onClick={() => openRecord(record._id)}><strong>{record.buyer || 'Customer'}</strong><span>{storeName(record.salesLocation)}</span><small>{[record.propertyAddress, record.propertyCity].filter(Boolean).join(', ')}</small></button>)}</div>
          {more && <p>Showing 50 customers. Narrow your search to find another customer.</p>}
        </>}
      </aside>
      <section className="mwo-editor mwo-card" aria-busy={loading || saving}>
        {loading && <p role="status">Opening work order…</p>}
        {!selected ? <div className="mwo-empty"><h2>Choose a customer to start</h2><p>The work order saves to their existing CRM contract.</p></div> : <>
          <div className="mwo-customer-header"><div><p className="mwo-kicker">SERVICE WORK ORDER</p><h2>{selected.buyer}</h2><p>{storeName(selected.salesLocation)}{address ? ' • ' + address : ''}</p><small>Contract: {String(selected._id)}</small></div><span className="mwo-state">{dirty ? 'Unsaved changes' : 'Saved version'}</span></div>
          <form onSubmit={submit}>
            <fieldset disabled={saving || loading}>
              {rows.map((row, index) => <div className="mwo-item" key={index}>
                <div className="mwo-item-title"><h3>Item {index + 1}</h3>{rows.length > 1 && <button type="button" className="mwo-text-button" onClick={() => { setRows(previous => previous.filter((_, i) => i !== index)); setDirty(true); setMessage(''); }}>Remove item {index + 1}</button>}</div>
                <div className="mwo-fields">{rowFields.map(key => <label key={key} className={key === 'Description' ? 'mwo-wide' : ''}>{key}{key === 'Description' ? ' *' : ''}{key === 'Description' ? <textarea aria-label={'Description ' + (index + 1)} required maxLength={5000} value={row[key] || ''} onChange={event => changeRow(index, key, event.target.value)} /> : <input aria-label={key + ' ' + (index + 1)} type={key === 'Hours' ? 'number' : 'text'} min={key === 'Hours' ? 0 : undefined} max={key === 'Hours' ? 100000 : undefined} step={key === 'Hours' ? 'any' : undefined} maxLength={5000} value={key === 'Location' ? showLocation(row[key]) : row[key] == null ? '' : row[key]} onChange={event => changeRow(index, key, event.target.value)} />}</label>)}</div>
              </div>)}
              <div className="mwo-actions"><button type="button" className="mwo-secondary" disabled={rows.length >= 200} onClick={() => { setRows(previous => [...previous, emptyRow()]); setDirty(true); setMessage(''); }}>+ Add item</button><button type="submit">{saving ? 'Saving…' : 'Save work order'}</button></div>
            </fieldset>
          </form>
          <div className="mwo-print">{selected.serviceOrderReport.map((row, index) => <section className="mwo-print-item" key={index}><h3>Item {index + 1}</h3><dl>{rowFields.map(key => <div key={key}><dt>{key}</dt><dd>{showLocation(row[key]) || '—'}</dd></div>)}</dl></section>)}</div>
          <div className="mwo-footer"><button type="button" className="mwo-text-button" disabled={saving || loading} onClick={() => openRecord(selected._id)}>Reload saved version</button><button type="button" className="mwo-secondary" disabled={dirty || saving || loading || !selected.serviceOrderReport.length} onClick={() => window.print()}>Print saved work order</button></div>
        </>}
      </section>
    </div>
  </main>;
}
